// Real WorkspacePort: every UI edit is committed to IndexedDB (entities + outbox in one transaction) before it
// counts as "saved on device". Commits are serialized; a failed commit reverts the view to the last durable
// state and reports an actionable error (storage full → export unsynced work).
import { ref } from 'vue';
import type { FileRef, Pane, Project, User } from '@/domain/model';
import type { Result, WorkspacePort } from '@/modules/panes/ports';
import { WorkspaceStore } from '@/db/workspaceStore';
import { LocalStorageFullError } from '@/db/localDb';
import { uuidv7 } from './ids';
import type { FileQueue } from '@/sync/files';
import { PROTOCOL_VERSION } from '@/sync/ops';

export type SaveState = 'saved' | 'saving' | 'error';
export interface StorageProblem { kind: 'storage_full' | 'write_failed'; message: string; exportUnsynced: () => Promise<string> }

export interface OfflineWorkspaceDeps {
  store: WorkspaceStore;
  workspaceId: string;
  deviceId: string;
  accounts: () => User[];
  onRevert: (projects: Project[]) => void;
  onCommitted?: () => void;
  /** offline attachment queue (P12) */
  files?: FileQueue;
  online?: {
    changePassword?(current: string, next: string): Promise<Result>;
    inviteToProject?(projectId: string, email: string): Promise<Result & { user?: User }>;
    removeProjectMember?(projectId: string, userId: string): Promise<Result>;
    signOut?(): void;
    switchAccount?(userId: string): void;
    saveProfile?(patch: { name?: string; email?: string }): void;
  };
}

export const saveState = ref<SaveState>('saved');
export const storageProblem = ref<StorageProblem | null>(null);

export function offlineWorkspace(d: OfflineWorkspaceDeps): WorkspacePort & { flushed(): Promise<void>; generation(): number; rebase(projects: Project[]): void } {
  let chain: Promise<void> = Promise.resolve();
  let durable: Project[] | null = null; // last state known to be committed
  let inflight = 0;
  let gen = 0; // increments on every enqueued commit (detects races with server reloads)
  const offlineOnly = async (): Promise<Result> => ({ ok: false, error: 'This action needs a connection' });

  return {
    commit(before, after, actorId, meta) {
      if (durable === null) durable = before;
      const snapshotAfter = JSON.parse(JSON.stringify(after)) as Project[];
      inflight++; gen++; saveState.value = 'saving';
      chain = chain.then(async () => {
        try {
          const base = durable!;
          await d.store.commit(base, snapshotAfter, { workspaceId: d.workspaceId, deviceId: d.deviceId, now: Date.now(), newId: uuidv7, activityGroup: meta?.activityGroup, actorId });
          durable = snapshotAfter;
          d.onCommitted?.();
        } catch (e) {
          const full = e instanceof LocalStorageFullError;
          storageProblem.value = { kind: full ? 'storage_full' : 'write_failed', message: full ? e.message : 'Your change could not be saved on this device.', exportUnsynced: () => d.store.exportUnsynced() };
          saveState.value = 'error';
          d.onRevert(JSON.parse(JSON.stringify(durable)));
        } finally {
          inflight--;
          if (!inflight && saveState.value !== 'error') saveState.value = 'saved';
        }
      });
      return { before, after: snapshotAfter };
    },
    undo(current, handles) {
      // Conditional inverse (CLAUDE.md §8): revert only fields whose current value still equals what this
      // edit produced, so colleagues' later changes are never overwritten by an old snapshot.
      let out = JSON.parse(JSON.stringify(current)) as Project[];
      for (const h of [...(handles as Array<{ before: Project[]; after: Project[] }>)].reverse()) out = revertConditionally(out, h.before, h.after);
      return out;
    },
    saveLayout(_panes: Pane[]) { /* pane views persisted per user/workspace in P08 */ },
    saveProfile: patch => d.online?.saveProfile?.(patch),
    changePassword: (c, n) => d.online?.changePassword?.(c, n) ?? offlineOnly(),
    inviteToProject: (p, e) => d.online?.inviteToProject?.(p, e) ?? offlineOnly(),
    removeProjectMember: (p, u) => d.online?.removeProjectMember?.(p, u) ?? offlineOnly(),
    attachFile: async (taskId: string, file: File, opts?: { role?: 'note_image' | 'comment_image' | 'project_comment_image'; ar?: number }): Promise<FileRef | null> => {
      if (!d.files) return null;
      try { return await d.files.enqueue(taskId, file, opts); }
      catch (e) {
        if (e instanceof LocalStorageFullError) { storageProblem.value = { kind: 'storage_full', message: e.message, exportUnsynced: () => d.store.exportUnsynced() }; return null; }
        throw e;
      }
    },
    removeFile: async (taskId: string, fileId: string): Promise<void> => { await d.files?.remove(taskId, fileId); },
    projectCommand: (projectId, command, baseRevision) => {
      // Serialized behind pending commits so FIFO order (and dependency order on the server) is preserved.
      gen++;
      const op = { protocol_version: PROTOCOL_VERSION, operation_id: uuidv7(), device_id: d.deviceId, workspace_id: d.workspaceId, entity_type: 'project' as const, entity_id: projectId,
        command_type: command, base_revision: baseRevision, expected: {}, payload: {}, depends_on: [], client_ts: new Date().toISOString() };
      const done = chain.then(async () => {
        try { await d.store.enqueue(op); d.onCommitted?.(); }
        catch (e) {
          const full = e instanceof LocalStorageFullError;
          storageProblem.value = { kind: full ? 'storage_full' : 'write_failed', message: full ? (e as Error).message : 'Your change could not be saved on this device.', exportUnsynced: () => d.store.exportUnsynced() };
          throw e;
        }
      });
      chain = done.catch(() => undefined);
      return done;
    },
    accounts: d.accounts,
    switchAccount: id => d.online?.switchAccount?.(id),
    signOut: () => d.online?.signOut?.(),
    newId: uuidv7,
    flushed: () => chain,
    generation: () => gen,
    /** Server data was merged into IndexedDB: the reloaded projection becomes the new durable base. */
    rebase: (projects: Project[]) => { durable = JSON.parse(JSON.stringify(projects)); },
  };
}

type Pos = { listId: string | null; index: number };
function locate(ps: Project[]): Map<string, { p: Project; pos: Pos; t: Project['lists'][number]['tasks'][number]; trash: boolean }> {
  const m = new Map();
  for (const p of ps) {
    p.lists.forEach(l => l.tasks.forEach((t, index) => m.set(t.id, { p, pos: { listId: l.id, index }, t, trash: false })));
    (p.trash || []).forEach((e, index) => m.set(e.task.id, { p, pos: { listId: null, index }, t: e.task, trash: true }));
  }
  return m;
}
const eq = (a: unknown, b: unknown) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

/** Applies before←after for every field of every task/list/project the edit touched, if still unchanged. */
export function revertConditionally(current: Project[], before: Project[], after: Project[]): Project[] {
  const cur = locate(current), bef = locate(before), aft = locate(after);
  const touched = new Set<string>();
  for (const [id, a] of aft) { const b = bef.get(id); if (!b || !eq(a.t, b.t) || a.trash !== b.trash || a.pos.listId !== b.pos.listId || a.pos.index !== b.pos.index) touched.add(id); }
  for (const id of bef.keys()) if (!aft.has(id)) touched.add(id);
  const FIELDS = ['title', 'notes', 'deadline', 'done', 'tags', 'assignees', 'checklist', 'files'] as const;
  for (const id of touched) {
    const a = aft.get(id), b = bef.get(id), c = cur.get(id);
    if (a && !b) { // created by the edit: remove only if nobody changed it since
      if (c && eq(c.t, a.t)) { if (c.trash) c.p.trash = c.p.trash!.filter(e => e.task.id !== id); else { const l = c.p.lists.find(x => x.id === c.pos.listId)!; l.tasks = l.tasks.filter(x => x.id !== id); } }
      continue;
    }
    if (!b) continue;
    if (!a) { // purged by the edit: bring back into trash (server keeps a short undo grace)
      if (!c) { const p = current.find(x => x.id === b.p.id); if (p) (p.trash = p.trash || []).push({ ...(before.find(x => x.id === b.p.id)!.trash!.find(e => e.task.id === id)!) }); }
      continue;
    }
    if (!c) continue;
    const R = (x: unknown) => x as Record<string, unknown>;
    for (const f of FIELDS) if (eq(R(c.t)[f], R(a.t)[f]) && !eq(R(a.t)[f], R(b.t)[f])) R(c.t)[f] = JSON.parse(JSON.stringify(R(b.t)[f] ?? null)) ?? undefined;
    const movedByEdit = a.trash !== b.trash || a.pos.listId !== b.pos.listId || a.pos.index !== b.pos.index;
    const stillThere = c.trash === a.trash && c.pos.listId === a.pos.listId;
    if (movedByEdit && stillThere) {
      // detach from current location
      if (c.trash) c.p.trash = c.p.trash!.filter(e => e.task.id !== id); else { const l = c.p.lists.find(x => x.id === c.pos.listId)!; l.tasks.splice(l.tasks.findIndex(x => x.id === id), 1); }
      if (b.trash) (c.p.trash = c.p.trash || []).push({ ...(before.find(x => x.id === b.p.id)!.trash!.find(e => e.task.id === id)!), task: c.t });
      else { const l = c.p.lists.find(x => x.id === b.pos.listId) || c.p.lists[0]!; l.tasks.splice(Math.min(b.pos.index, l.tasks.length), 0, c.t); }
    }
  }
  // project and list fields
  for (const pa of after) {
    const pb = before.find(x => x.id === pa.id), pc = current.find(x => x.id === pa.id);
    if (!pc) continue;
    if (!pb) { if (pc.lists.every(l => !l.tasks.length) && eq(pc.name, pa.name)) current.splice(current.indexOf(pc), 1); continue; }
    for (const f of ['name', 'notes', 'tagOrder', 'filterOrder', 'nicks', 'deletedAt', 'deletedBy'] as const) if (eq(pc[f], pa[f]) && !eq(pa[f], pb[f])) (pc as unknown as Record<string, unknown>)[f] = JSON.parse(JSON.stringify(pb[f] ?? null)) ?? undefined;
    for (const la of pa.lists) {
      const lb = pb.lists.find(x => x.id === la.id), lc = pc.lists.find(x => x.id === la.id);
      if (!lc) continue;
      if (!lb) { if (!lc.tasks.length) pc.lists.splice(pc.lists.indexOf(lc), 1); continue; }
      if (lc.title === la.title && la.title !== lb.title) lc.title = lb.title;
    }
    for (const lb of pb.lists) if (!pa.lists.some(x => x.id === lb.id) && !pc.lists.some(x => x.id === lb.id)) {
      // list deleted by the edit: recreate it and pull back tasks still where the delete put them
      const nl = { ...lb, tasks: [] as typeof lb.tasks };
      pc.lists.splice(Math.min(pb.lists.indexOf(lb), pc.lists.length), 0, nl);
      for (const t of lb.tasks) { const now = locate([pc]).get(t.id); if (!now) continue; if (now.trash) pc.trash = pc.trash!.filter(e => e.task.id !== t.id); else { const l = pc.lists.find(x => x.id === now.pos.listId)!; l.tasks.splice(l.tasks.findIndex(x => x.id === t.id), 1); } nl.tasks.push(now.t); }
    }
  }
  return current;
}
